import ScriptingEffect from '../scripting/ScriptingEffect.js';
import NodeMessaging from "messaging/NodeMessaging.js";
import {RESET, CYCLE_RESTART, SCALE} from "messaging/message-types.js";
import {saveParameterToJson, loadParameterFromJson} from 'parameters/parameterJsonFunctions.js';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import OptionParameter from 'parameters/OptionParameter.js';
import {SCALE_TO_ARRAY, MAJOR, MINOR, HARMONIC_MINOR} from './scales.js';
import {mod} from "utils/mod.js";
import {atranspose} from '../scripting/utils.js';
import {IntegerParameter, AbstractParameter} from "parameters/AbstractParameter.js";
import ChordsRack from './ChordsRack.js';
import {t_truncate} from '../scripting/tlist.js';
import {vary_every} from '../scripting/tevery.js';
import {add} from '../scripting/lazy.js';
import {chord_express, invert_chord, gen_chord, scl, t_put_scale} from '../scripting/t_harmony.js';
import {chord} from '../scripting/harmonize.js';
import {nth, list, sublist, len, rconcat, lconcat, concat} from '../scripting/list.js';
import {shift} from '../scripting/beat.js';
import {t_trigger} from '../scripting/triggerAttackRelease.js';
import {rules, when, ACCENT, REST} from '../scripting/trules.js';
import {pipe} from '../scripting/pipe.js';
import {
    filter,
    s,
    switchOutput,
    execute,
    rolling,
    notes,
    steps,
    prob,
    cond,
    p,
    lazy
} from '../scripting/lang.js';
import {hand, t_print, t_cond, j_tidal, tidal, tparse} from '../scripting/tidal.js';
import {parse, getCycleString} from 'effects/midi/tidal/parser.js';

window.chordTimes = [];

const f7 = list(
  tparse('- - . -'),
  tparse('- . - -'),
  tparse('. - - -'),
  tparse('- - - .'));

const f5 = list(
  tparse('- - .'),
  tparse('- . -'),
  tparse('. - -')
  );

export default class GenerativeChordsEffect extends NodeMessaging(ScriptingEffect) {

    constructor(track) {
        super("Generative Chords", track);

        this.chordProgression = new ChordsRack(track, this);

        this.progressionLengthParameter = new IntegerParameter(
            "progressionLength", this, () => [],
            {
                defaultValue: 4,
                min: 1,
                max: 16
            });
        this.registerParameter(this.progressionLengthParameter);
        this.currentChord = 1;
        this.tidalString = '. - . -';

        this.expressionSeries = [13, 11, 9, 7];
        this.scaleParameter = new IntegerParameter(
            'scale',
            this, () => [],
            {
                defaultValue: MAJOR,
                min: 1,
                max: 3,
            });
        
        this.registerParameter(this.scaleParameter);

        this.connectedJakiEffect = undefined;

        this.newInlet(SCALE);
        this.newInlet(RESET, () => this.reset());
        this.newOutlet(CYCLE_RESTART);
    }

    reset() {
        this.position = undefined;
    }

    getParameters() {
        return [... this.parameters, ... this.chordProgression.getParameters()];
    }

    get tidalString() {
        return this._tidalString;
    }

    set tidalString(s) {
        this.tidalPattern = tparse(s)();
        this._tidalString = s;
    };
    
    getNoteDegree(note) {
        note = mod(note, 12);
        let scale = this.shifted_scale();
        let index = scale.indexOf(note);
        return index % 2 === 0 ? index + 1 : index + 8;
    }

    getCurrentChordNotes() {
        return [];
    }

    get scale() {
        return this.scaleParameter.get();
    }

    set scale(x) {
        this.scaleParameter.set(x, false);
    }

    get progressionLength() {
        return this.progressionLengthParameter.get();
    }

    getPossibleNextChords(chord=this.currentChord) {
        return this.chordProgression.getPossibleNextChords(chord);
    }

    getNextChord(chord=this.currentChord, position=this.position) {
        return this.chordProgression.getNextChord(
            chord, position);
    }

    get chordElements() {
        return this.chordProgression.getFlowChordVoicing(() => this.currentChord);
    }

    output(...args) {
        let a = new Date().getTime();
        let hasTriggers = this.triggers.length > 0;
        let x = super.output(...args);
        let b = new Date().getTime();
        if (hasTriggers) {
            window.chordTimes.push(b-a);
        }
        return x;
    }

    clear() {
        if (this.tidals) {
            this.tidals.forEach(
                t => t.clear());
        }

        this.freeTidals();
        this.chordProgression.clear();

        // position keeps track of where in the chord progression
        // we are. we begin at "undefined" because we are starting
        this.position = undefined;


        let scale = () => {
            if (this.isInletConnected(SCALE)) {
                let msgs = this.receiveMessage(SCALE);
                // get the top message
                if (msgs[0]) {
                    let [_scale] = msgs[0];
                    this._scale = _scale;
                    return _scale;
                } else if (this._scale) {
                    return this._scale;
                }
            }
            return SCALE_TO_ARRAY[this.scale];
        };
        this.shifted_scale = shift(scale, () => this.currentChord - 1);

        const changeChordsIfNeeded = () => {
            return (...args) => {
                if (args[0]) {
                    // keep track of what nnth chord we are on
                    if (this.position === undefined) {
                        this.position = 0;
                        this.currentChord = this.chordProgression.getStartingChord();
                        this.sendMessage(CYCLE_RESTART);
                    } else {
                        this.position++;
                        if (this.position >= this.progressionLength) {
                            this.position = 0;
                            this.currentChord = this.chordProgression.getStartingChord();
                            this.sendMessage(CYCLE_RESTART);
                        } else {
                            this.currentChord = this.getNextChord();
                        }
                    }
                }
                return filter(true);
            };
        }; 

        let tran = 0;

        this.myScript = pipe(
            (stepData) => {
                if (stepData) {
                    tran = stepData.transpose;
                }
                return filter(true);
            },
            changeChordsIfNeeded(),
            gen_chord(
                this.shifted_scale, this.chordElements, () => tran),
        );

    }

    getCopy(track, ignoreId=true) {
        let fx = new GenerativeChordsEffect(track);
        fx.fromJson(fx.getJson(), ignoreId);
        return fx;
    }

    fromJson(json) {
        let bulk = super.fromJson(json);

        bulkUpdate(this.scaleParameter, json.scale, bulk);
        
        bulk = {
            ... bulk,
            ... loadParameterFromJson(json, 'scaleParameter', this.scaleParameter)
        };

        bulkUpdate(this.progressionLengthParameter, json.progressionLength, bulk);
        bulk = {
            ... bulk,
            ... loadParameterFromJson(json, 'progressionLengthParameter', this.progressionLengthParameter)
        };

        if (json.chordProgression) {
            this.chordProgression.effects = [];
            bulk =
                {
                    ... bulk,
                    ... this.chordProgression.fromJson(json.chordProgression)
                };
        }
        return bulk;
    }

    getJson() {
        return {
            ... super.getJson(),
            chordProgression: this.chordProgression.getJson()
        };
    }
}
